import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Navbar } from "@/components/Navbar";
import { ProductCard } from "@/components/ProductCard";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Dog, Ruler } from "lucide-react";
import { SEOTags } from "@/components/SEOTags";
import { useProductReviews } from "@/hooks/use-product-reviews";
import { LoadingPage } from "@/components/LoadingPage";
import { useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faBowlFood, 
  faHeartPulse, 
  faBone, 
  faDog, 
  faShieldDog, 
  faList
} from "@fortawesome/free-solid-svg-icons";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { CategorySidebar } from "@/components/CategorySidebar";
import { CategorySEOContent } from "@/components/seo/CategorySEOContent";
import { CTABanner } from "@/components/cta/CTABanner";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

const QUICK_ACCESS_CATEGORIES = [
  { name: 'Food & Treats', icon: faBowlFood, simple_id: 6 },
  { name: 'Health', icon: faHeartPulse, simple_id: 4 },
  { name: 'Training', icon: faShieldDog, simple_id: 3 },
  { name: 'Gear', icon: faDog, simple_id: 25 },
  { name: 'Toys', icon: faBone, simple_id: 5 },
  { name: 'All Products', icon: faList, simple_id: 1 }
];

const PRICE_RANGES = [
  { label: "Under £10", min: 0, max: 10 },
  { label: "£10 - £20", min: 10, max: 20 },
  { label: "£20 - £50", min: 20, max: 50 },
  { label: "£50 - £100", min: 50, max: 100 },
  { label: "Over £100", min: 100, max: null }
];

const CategoryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const findCategoryId = () => {
    if (/^\d+$/.test(id || '')) {
      return parseInt(id!, 10);
    }
    
    const category = QUICK_ACCESS_CATEGORIES.find(cat => 
      cat.name.toLowerCase() === decodeURIComponent(id || '').toLowerCase()
    );
    
    return category?.simple_id || 1;
  };
  
  const numericId = findCategoryId();
  
  const [sortOrder, setSortOrder] = useState<string>("default");
  const [selectedPriceRange, setSelectedPriceRange] = useState<string>("all");
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>("all");
  const [foodTreatsFilter, setFoodTreatsFilter] = useState<string>("all");

  useEffect(() => {
    setSelectedSubCategory("all");
    setFoodTreatsFilter("all");
  }, [numericId]);

  useEffect(() => {
    if (id && !/^\d+$/.test(id)) {
      console.log('Redirecting from name-based URL to ID-based URL', numericId);
      navigate(`/category/${numericId}`, { replace: true });
    }
  }, [id, navigate, numericId]);

  const { data: category, isLoading: categoryLoading } = useQuery({
    queryKey: ['category', numericId],
    queryFn: async () => {
      console.log('Fetching category with simple_id:', numericId);
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('simple_id', numericId)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!numericId,
  });

  const { data: subCategories, isLoading: subCategoriesLoading } = useQuery({
    queryKey: ['subcategories', category?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('parent_id', category?.id);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!category?.id,
  });

  const { data: products, isLoading: productsLoading } = useQuery({
    queryKey: ['category-products', category?.id, selectedSubCategory, foodTreatsFilter],
    queryFn: async () => {
      let query = supabase
        .from('products')
        .select('*, product_images(*)');
      
      if (category?.simple_id !== 1) {
        query = query.eq('category', category?.name);
        
        if (selectedSubCategory !== 'all') {
          query = query.eq('sub_category', selectedSubCategory);
        }

        if (category?.simple_id === 6 && foodTreatsFilter !== 'all') {
          if (foodTreatsFilter === 'food') {
            // More comprehensive food filtering - include dry food, wet food, kibble, etc.
            query = query.or('name.ilike.%food%,name.ilike.%kibble%,name.ilike.%meal%,name.ilike.%diet%,name.ilike.%nutrition%,sub_category.ilike.%food%');
          } else if (foodTreatsFilter === 'treats') {
            // More comprehensive treats filtering - include all treat variations
            query = query.or('name.ilike.%treat%,name.ilike.%biscuit%,name.ilike.%snack%,name.ilike.%chew%,name.ilike.%bone%,name.ilike.%reward%,sub_category.ilike.%treat%');
          }
        }
      }

      const { data, error } = await query;
      
      if (error) throw error;
      console.log('Fetched products with images:', data);
      return data || [];
    },
    enabled: !!category,
  });

  const productIds = products?.map(product => product.id) || [];
  const { reviews, isLoading: reviewsLoading } = useProductReviews(productIds);

  const filteredProducts = products?.filter(product => {
    if (selectedPriceRange === 'all') return true;
    
    const price = product.price;
    switch (selectedPriceRange) {
      case 'Under £10':
        return price < 10;
      case '£10 - £20':
        return price >= 10 && price <= 20;
      case '£20 - £50':
        return price >= 20 && price <= 50;
      case '£50 - £100':
        return price >= 50 && price <= 100;
      case 'Over £100':
        return price > 100;
      default:
        return true;
    }
  });

  const sortedProducts = filteredProducts?.sort((a, b) => {
    switch (sortOrder) {
      case "price-asc":
        return a.price - b.price;
      case "price-desc":
        return b.price - a.price;
      default:
        return 0;
    }
  });

  const handleCategorySelect = (categoryId: number) => {
    navigate(`/category/${categoryId}`);
  };

  if (categoryLoading) {
    return <LoadingPage />;
  }

  const isGearCategory = numericId === 25;
  const isFoodTreatsCategory = numericId === 6;

  return (
    <div className="min-h-screen">
      <SEOTags
        title={`${category?.name || 'Products'} - Roselands Pet Supplies Northern Ireland`}
        description={`Shop ${category?.name || 'premium pet products'} at Roselands. Serving Belfast, Lurgan, Armagh, Banbridge, Portadown, Waringstown (BT60, BT66). Quality pet supplies in Northern Ireland.`}
        keywords={`${category?.name?.toLowerCase() || 'pet products'} Northern Ireland, ${category?.name?.toLowerCase() || 'pet supplies'} Belfast, ${category?.name?.toLowerCase() || 'pet products'} Lurgan, ${category?.name?.toLowerCase() || 'pet supplies'} Armagh`}
        path={`/category/${numericId}`}
      />
      <Navbar />
      <div className="container mx-auto px-0 sm:px-4 py-8 mt-16 sm:mt-20 flex max-w-[100vw] overflow-x-hidden">
        {/* Desktop Sidebar */}
        <div className="hidden md:block sticky top-24 h-[calc(100vh-6rem)] self-start">
          <CategorySidebar 
            activeCategory={numericId}
            onSelectCategory={handleCategorySelect}
            onSelectPriceRange={setSelectedPriceRange}
            activePriceRange={selectedPriceRange}
            subCategories={subCategories}
            onSelectSubCategory={setSelectedSubCategory}
            activeSubCategory={selectedSubCategory}
          />
        </div>

        <div className="flex-1 px-4 md:px-6 w-full">
          {/* Breadcrumb navigation */}
          <Breadcrumb className="mb-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{category?.name || 'Products'}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          
          <div className="flex items-center mb-6">
            <Button
              variant="ghost"
              className="hover:bg-red-50 hover:text-red-500"
              onClick={() => navigate('/')}
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold ml-4">
              {category ? category.name : 'All Products'}
            </h1>
          </div>

          {/* Food & Treats Filter - Desktop */}
          {isFoodTreatsCategory && !isMobile && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Filter by Type</h3>
              <ToggleGroup 
                type="single" 
                value={foodTreatsFilter} 
                onValueChange={(value) => setFoodTreatsFilter(value || "all")}
                className="justify-start"
              >
                <ToggleGroupItem 
                  value="all" 
                  className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  All Items
                </ToggleGroupItem>
                <ToggleGroupItem 
                  value="food" 
                  className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  Food
                </ToggleGroupItem>
                <ToggleGroupItem 
                  value="treats" 
                  className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  Treats
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          )}

          {/* Size Chart Call-to-Action for Gear Category */}
          {isGearCategory && (
            <div className="mb-8 bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 p-6 rounded-xl border border-indigo-100 shadow-sm animate-fade-up">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-indigo-100 p-2 rounded-lg">
                  <Ruler className="h-6 w-6 text-indigo-600" />
                </div>
                <h2 className="text-xl font-semibold text-indigo-800">Find the Perfect Fit</h2>
              </div>
              <p className="text-indigo-700 leading-relaxed mb-4">
                Not sure which size to order? Check our size charts to ensure the perfect fit for your furry friend.
              </p>
              <Button 
                onClick={() => navigate('/size-guide')}
                variant="outline" 
                className="bg-white hover:bg-indigo-50 text-indigo-700 border-indigo-200 hover:border-indigo-300"
              >
                <Ruler className="mr-2 h-4 w-4" />
                View Size Charts
              </Button>
            </div>
          )}

          {/* Mobile Quick Access Category Carousel */}
          <div className="block md:hidden mb-8 w-full">
            <Carousel
              opts={{
                align: "center",
                loop: true,
              }}
              className="w-full relative"
            >
              <CarouselContent>
                {QUICK_ACCESS_CATEGORIES.map((cat) => (
                  <CarouselItem key={cat.simple_id} className="basis-1/2 sm:basis-1/3">
                    <Link 
                      to={`/category/${cat.simple_id}`}
                      className={`flex flex-col items-center p-4 bg-white rounded-xl transition-all duration-300 hover:translate-y-[-4px] min-w-[100px] ${
                        numericId === cat.simple_id ? 'bg-red-50' : ''
                      }`}
                    >
                      <div className="w-12 h-12 bg-primary/5 rounded-lg flex items-center justify-center mb-2 group-hover:bg-primary/10 transition-colors">
                        <FontAwesomeIcon 
                          icon={cat.icon} 
                          className="text-primary" 
                          size="lg" 
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900 whitespace-nowrap">{cat.name}</span>
                    </Link>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="absolute left-0 top-1/2 -translate-y-1/2" />
              <CarouselNext className="absolute right-0 top-1/2 -translate-y-1/2" />
            </Carousel>
          </div>

          <div className="mb-8 space-y-4">
            <div className="flex flex-wrap gap-4">
              {/* Sort dropdown */}
              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger className="bg-white border-red-100 focus:ring-red-100 min-w-[180px] w-full max-w-[180px]">
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="default" className="focus:bg-red-50 focus:text-red-500">
                    Default
                  </SelectItem>
                  <SelectItem value="price-asc" className="focus:bg-red-50 focus:text-red-500">
                    Price: Low to High
                  </SelectItem>
                  <SelectItem value="price-desc" className="focus:bg-red-50 focus:text-red-500">
                    Price: High to Low
                  </SelectItem>
                </SelectContent>
              </Select>

              {/* Mobile price filter dropdown */}
              <div className="md:hidden">
                <Select value={selectedPriceRange} onValueChange={setSelectedPriceRange}>
                  <SelectTrigger className="bg-white border-red-100 focus:ring-red-100 min-w-[180px] w-full max-w-[180px]">
                    <SelectValue placeholder="Filter by price" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="all" className="focus:bg-red-50 focus:text-red-500">
                      All Prices
                    </SelectItem>
                    {PRICE_RANGES.map((range) => (
                      <SelectItem 
                        key={range.label} 
                        value={range.label}
                        className="focus:bg-red-50 focus:text-red-500"
                      >
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Mobile subcategory filter dropdown if available */}
              {subCategories && subCategories.length > 0 && (
                <div className="md:hidden">
                  <Select value={selectedSubCategory} onValueChange={setSelectedSubCategory}>
                    <SelectTrigger className="bg-white border-red-100 focus:ring-red-100 min-w-[180px] w-full max-w-[180px]">
                      <SelectValue placeholder="Subcategory" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all" className="focus:bg-red-50 focus:text-red-500">
                        All Subcategories
                      </SelectItem>
                      {subCategories.map((subCat) => (
                        <SelectItem 
                          key={subCat.id} 
                          value={subCat.name}
                          className="focus:bg-red-50 focus:text-red-500"
                        >
                          {subCat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Mobile Food/Treats filter - simplified and styled */}
              {isFoodTreatsCategory && isMobile && (
                <div className="w-full">
                  <Select value={foodTreatsFilter} onValueChange={setFoodTreatsFilter}>
                    <SelectTrigger className="bg-white border-red-100 focus:ring-red-100 w-full">
                      <SelectValue placeholder="Filter by type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all" className="focus:bg-red-50 focus:text-red-500">
                        🐾 All Items
                      </SelectItem>
                      <SelectItem value="food" className="focus:bg-red-50 focus:text-red-500">
                        🍽️ Food
                      </SelectItem>
                      <SelectItem value="treats" className="focus:bg-red-50 focus:text-red-500">
                        🦴 Treats
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* SEO Content Section */}
          <div className="mb-8">
            {category?.name === "Food & Treats" && <CategorySEOContent category="treats" />}
            {category?.name === "Health" && <CategorySEOContent category="health" />}
            {category?.name === "Training" && <CategorySEOContent category="training" />}
            {category?.name === "Accessories" && <CategorySEOContent category="grooming" />}
            {category?.name === "Toys" && <CategorySEOContent category="toys" />}
            {(category?.name === "All Products" || !category?.name) && <CategorySEOContent category="dogs" />}
          </div>

          {/* CTA Banner */}
          <div className="mb-8">
            <CTABanner variant="newsletter" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedProducts && sortedProducts.length > 0 ? (
              sortedProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  id={product.id}
                  title={product.name}
                  price={product.price}
                  image={product.image_url || "/placeholder.svg"}
                  rating={4}
                  description={product.description}
                  brand={product.brand}
                  is_preorder={product.is_preorder}
                  preorder_price={product.preorder_price}
                  preorder_release_date={product.preorder_release_date}
                  preorder_available_quantity={product.preorder_available_quantity}
                  reviews={reviews[product.id] || []}
                  stock_count={product.stock_count}
                  sub_category={product.sub_category}
                  product_images={product.product_images}
                />
              ))
            ) : (
              <p className="col-span-full text-center text-gray-500 py-8">
                No products found matching your criteria
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;
